import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, LockKey, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "../lib/api";

export default function AdminResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirmPw) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset — you can log in now.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-brandblue-ink text-white flex items-center justify-center px-4" data-testid="admin-reset-password-screen">
      <div aria-hidden className="absolute inset-0 grid-bg opacity-70" />
      <div aria-hidden className="orb h-[520px] w-[520px] bg-brandblue/40 -top-20 -left-20" />
      <div aria-hidden className="orb h-[420px] w-[420px] bg-ova/30 bottom-0 right-0" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-20 w-full max-w-md"
      >
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl p-8 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.6)]">
          <Link to="/admin/login" className="inline-flex items-center gap-2" data-testid="reset-password-home-link">
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-md bg-transparent overflow-hidden">
              <img src="/logo.png" alt="MedexOVA" className="h-8 w-8 object-contain logo-glow" />
            </span>
            <span className="font-heading text-lg tracking-tight font-black">
              <span className="text-[#16A34A]">Medex</span><span className="text-[#C4B5FD]">OVA</span>
            </span>
          </Link>

          <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-brandblue/30 bg-brandblue/10 px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-brandblue">
            <LockKey size={12} weight="bold" /> Password reset
          </div>

          {!token ? (
            <div className="mt-8" data-testid="reset-password-missing-token">
              <h2 className="font-heading text-2xl font-bold">Invalid link</h2>
              <p className="mt-2 text-sm text-white/60">This reset link is missing its token. Request a new one from the sign-in page.</p>
              <Link to="/admin/login" className="mt-6 inline-block text-xs font-bold uppercase tracking-wider text-brandblue hover:text-brandblue-deep" data-testid="reset-password-back-link">
                Back to sign-in
              </Link>
            </div>
          ) : done ? (
            <div className="mt-8 text-center" data-testid="reset-password-success">
              <h2 className="font-heading text-2xl font-bold">Password updated</h2>
              <p className="mt-2 text-sm text-white/60">Your admin password has been reset.</p>
              <button
                type="button"
                onClick={() => nav("/admin/login")}
                data-testid="reset-password-login-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brandblue to-ova hover:from-brandblue-deep hover:to-ova-hover text-white px-6 py-3 text-sm font-semibold transition-all"
              >
                Go to sign-in <ArrowRight weight="bold" size={14} />
              </button>
            </div>
          ) : (
            <>
              <h2 className="mt-5 font-heading text-3xl font-bold leading-tight">Set a new password.</h2>
              <p className="mt-2 text-sm text-white/60">Must be at least 8 characters.</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-reset-password-form">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2">New password</label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 pr-11 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-brandblue/40 focus:border-brandblue transition"
                      placeholder="••••••••"
                      data-testid="reset-password-new-input"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center text-white/50 hover:text-white"
                      data-testid="reset-password-toggle"
                      aria-label="Toggle password visibility"
                    >
                      {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2">Confirm password</label>
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    minLength={8}
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    className="w-full rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-brandblue/40 focus:border-brandblue transition"
                    placeholder="••••••••"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="reset-password-submit-btn"
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brandblue to-ova hover:from-brandblue-deep hover:to-ova-hover disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold transition-all"
                >
                  {submitting ? "Saving…" : "Reset password"} <ArrowRight weight="bold" size={14} />
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </main>
  );
}
